import type { ColDef } from "@performyard/py-components";

import type { DecisionRow } from "../../types";

import {
	BudgetReviewRowEditCellRenderer,
	CurrentSalaryCellRenderer,
	EmployeeAvatarCellRenderer,
	EmployeeInfoCellRenderer,
	EmployeeInfoCellRendererBudgetReviews,
	IncreaseAmountCellRenderer,
	IncreasePercentCellRenderer,
	NewSalaryCellRenderer,
	SuggestedIncreaseCellRenderer,
} from "../CellRenderers";

/** 4px horizontal padding on each cell/header cell so adjacent columns are separated by 8px. */
export const DECISION_TABLE_COLUMN_GAP_CLASSNAME = "[&_.ag-cell]:!px-1 [&_.ag-header-cell]:!px-1";

/** Applied to Employee Info column cell/header in budget review read-only defs (keep in sync with {@link BUDGET_REVIEW_READ_ONLY_TABLE_EDGE_PADDING_CLASSNAME}). */
export const BUDGET_REVIEW_EMPLOYEE_INFO_CELL_CLASSNAME = "budget-review-employee-info-cell";

/**
 * Budget review read-only grid: 16px inset left of the Employee column and right of Employee Info
 * (selectors beat {@link DECISION_TABLE_COLUMN_GAP_CLASSNAME} and Table.css first/last padding).
 */
export const BUDGET_REVIEW_READ_ONLY_TABLE_EDGE_PADDING_CLASSNAME =
	"[&_.ag-cell.ag-column-first]:!pl-1 [&_.ag-header-cell.ag-column-first]:!pl-1";

export const READ_ONLY_TABLE_CLASSNAME = `border-t border-solid border-neutral-2 bg-neutral-0 [&_.ag-header]:!bg-white [&_.ag-root-wrapper]:!border-none ${DECISION_TABLE_COLUMN_GAP_CLASSNAME} ${BUDGET_REVIEW_READ_ONLY_TABLE_EDGE_PADDING_CLASSNAME}`;

export const EDITABLE_TABLE_CLASSNAME = `[&_.ag-header]:!bg-white [&_.ag-root-wrapper]:!border-none ${DECISION_TABLE_COLUMN_GAP_CLASSNAME}`;

export const BUDGET_REVIEW_TABLE_CLASSNAME = `min-w-0 max-w-full border-t border-solid border-neutral-2 bg-neutral-0 [&_.ag-header]:!bg-white [&_.ag-root-wrapper]:!border-none ${DECISION_TABLE_COLUMN_GAP_CLASSNAME} ${BUDGET_REVIEW_READ_ONLY_TABLE_EDGE_PADDING_CLASSNAME}`;

// --- Shared column definitions ---

const employeeCol: ColDef<DecisionRow> = {
	headerName: "Employee",
	field: "employee_name",
	cellRenderer: EmployeeAvatarCellRenderer,
	width: 205,
	sortable: true,
};

const currentSalaryCol: ColDef<DecisionRow> = {
	headerName: "Current Salary",
	field: "current_salary",
	cellRenderer: CurrentSalaryCellRenderer,
	width: 110,
	sortable: true,
};

const suggestedCol: ColDef<DecisionRow> = {
	headerName: "Suggested",
	sortable: false,
	width: 110,
	cellRenderer: SuggestedIncreaseCellRenderer,
};

const increasePercentCol: ColDef<DecisionRow> = {
	headerName: "Increase",
	field: "proposed_increase_percent",
	cellRenderer: IncreasePercentCellRenderer,
	width: 110,
	sortable: false,
};

const increaseAmountCol: ColDef<DecisionRow> = {
	headerName: "Increase",
	field: "proposed_increase_amount",
	width: 110,
	cellRenderer: IncreaseAmountCellRenderer,
	sortable: false,
};

const newSalaryCol: ColDef<DecisionRow> = {
	headerName: "New Salary",
	field: "proposed_salary",
	cellRenderer: NewSalaryCellRenderer,
	width: 110,
	sortable: true,
};

export const expandedDecisionTableColumnDefs: ColDef<DecisionRow>[] = [
	employeeCol,
	currentSalaryCol,
	suggestedCol,
	{
		headerName: "Suggested",
		width: 110,
		sortable: false,
		cellRenderer: SuggestedIncreaseCellRenderer,
	},
	increasePercentCol,
	increaseAmountCol,
	{
		headerName: "Employee Info",
		cellRenderer: EmployeeInfoCellRenderer,
		width: 110,
		sortable: false,
		resizable: false,
	},
	newSalaryCol,
];

/** Read-only budget review: salary columns plus Employee Info (same three actions as My Decisions). */
export const budgetReviewReadOnlySalaryColumnDefs: ColDef<DecisionRow>[] = [
	{ ...employeeCol },
	{ ...currentSalaryCol },
	{ ...suggestedCol },
	{ ...increasePercentCol },
	{ ...increaseAmountCol },
	{ ...newSalaryCol, width: 110 },
	{
		headerName: "Employee Info",
		colId: "budgetReviewEmployeeInfo",
		cellClass: BUDGET_REVIEW_EMPLOYEE_INFO_CELL_CLASSNAME,
		headerClass: BUDGET_REVIEW_EMPLOYEE_INFO_CELL_CLASSNAME,
		cellRenderer: EmployeeInfoCellRendererBudgetReviews,
		width: 120,
		minWidth: 120,
		sortable: false,
		resizable: false,
	},
	{
		headerName: "",
		colId: "budgetReviewReadOnlyBlank",
		width: 32,
		sortable: false,
		resizable: false,
	},
];

/** Cycle must be active (not scheduled or completed) and budgets must be - at least - submited */
export const budgetReviewManagerGroupStatusShowsRowEdit = (status: string, cycleStatus: string): boolean =>
	cycleStatus === "active" && (status === "in_review" || status === "approved");

/** Same as {@link budgetReviewReadOnlySalaryColumnDefs} but replaces the trailing blank column with an Edit column. */
export const budgetReviewSalaryColumnDefsWithEdit: ColDef<DecisionRow>[] = [
	...budgetReviewReadOnlySalaryColumnDefs.filter((col) => col.colId !== "budgetReviewReadOnlyBlank"),
	{
		headerName: "",
		colId: "budgetReviewRowEdit",
		width: 48,
		minWidth: 48,
		flex: 1,
		sortable: false,
		resizable: false,
		cellClass: "!overflow-visible",
		cellRenderer: BudgetReviewRowEditCellRenderer,
	},
];
